import { apiErrorMessage } from './prescriptionForm';
import { MIN_PASSWORD_LENGTH } from './authForm';

/**
 * Pure logic for the new-password screen (ResetPassword.js).
 *
 * See forgotForm.js for why this is not called resetPassword.
 */

/** Blank starting state for the new-password form. */
export const EMPTY_RESET = {
  password: '',
  confirmPassword: '',
};

/**
 * Pull the reset token out of a location search string.
 *
 * Returns '' when there is none, which the screen treats as a broken link
 * rather than rendering a form that cannot possibly succeed. Uses
 * URLSearchParams so a token containing base64url characters survives — the
 * token is 43 characters of [A-Za-z0-9_-] and hand-rolled splitting on '='
 * would truncate nothing today but breaks the moment padding appears.
 */
export function tokenFromSearch(search) {
  return new URLSearchParams(String(search ?? '')).get('token') ?? '';
}

/**
 * Validate the new-password form, returning field-keyed errors.
 *
 * The length floor is MIN_PASSWORD_LENGTH, imported rather than repeated: the
 * server enforces min=6 on this endpoint too, and a client with its own number
 * would either reject passwords the server accepts or promise ones it rejects.
 */
export function validateResetForm(form = {}) {
  const errors = {};

  // Not trimmed — leading and trailing spaces are legitimate password
  // characters, and stripping them would set a password the user did not type.
  const password = String(form.password ?? '');
  if (password === '') {
    errors.password = 'Choose a password.';
  } else if (password.length < MIN_PASSWORD_LENGTH) {
    errors.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  }

  const confirmPassword = String(form.confirmPassword ?? '');
  if (confirmPassword === '') {
    errors.confirmPassword = 'Repeat your password.';
  } else if (password !== confirmPassword) {
    errors.confirmPassword = 'Passwords do not match.';
  }

  return errors;
}

/**
 * Map a failed reset to something the user can act on.
 *
 * A 400 is the single answer the server gives for unknown, expired and already
 * used (api-contract.md AUTH-20). The client must not guess between them — it
 * has no more information than the server chose to give — so it names all three
 * possibilities and offers the one action that resolves every one of them.
 */
export function resetErrorMessage(error) {
  if (!error?.response) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  if (error.response.status === 400) {
    return 'That reset link has expired or has already been used. Request a new one.';
  }
  if (error.response.status === 404) {
    return 'Password reset is not available on this deployment. Ask your coach to reset it for you.';
  }
  return apiErrorMessage(error, 'Something went wrong resetting your password.');
}
